import React, { useEffect, useState } from "react";
import client, { apiError } from "@/lib/api";
import { money } from "@/lib/format";
import { PageHeader } from "@/components/shared";
import { PlatformMonthlyTable, PlatformMonthRow } from "@/components/PlatformMonthlyTable";
import { PlatformChargesDetail } from "@/components/PlatformChargesDetail";
import { toast } from "sonner";
import { Loader2, RefreshCw } from "lucide-react";

interface Data {
  months: PlatformMonthRow[];
  pending_now: { shopify: number; mercadolibre: number; amazon: number; total: number };
}

const ReconciliationPage: React.FC = () => {
  const [data, setData] = useState<Data | null>(null);
  const [loading, setLoading] = useState(true);
  const [rebuilding, setRebuilding] = useState(false);
  const [sel, setSel] = useState<{ month: string; channel: string } | null>(null);

  const load = () => {
    setLoading(true);
    client.get("/reconciliation/platforms").then((r) => setData(r.data)).finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const rebuild = async () => {
    setRebuilding(true);
    try {
      const r = await client.post("/platform-deposits/rebuild");
      toast.success(`Depósitos actualizados (${r.data.count})`);
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setRebuilding(false);
    }
  };

  return (
    <div data-testid="reconciliation-page">
      <PageHeader
        title="Conciliación de plataformas"
        subtitle="Lo vendido mes a mes en cada plataforma, sus cargos y lo que realmente te depositaron"
        actions={
          <button
            onClick={rebuild}
            disabled={rebuilding}
            data-testid="rebuild-deposits-button"
            className="flex items-center gap-2 rounded-none border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 disabled:opacity-60"
          >
            {rebuilding ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            Actualizar depósitos
          </button>
        }
      />
      {loading || !data ? (
        <div className="flex h-64 items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-[#DD0000]" />
        </div>
      ) : (
        <>
          <div className="mb-6 grid grid-cols-2 gap-4 md:grid-cols-4" data-testid="pending-now">
            {([["Pendiente por depositar hoy", data.pending_now.total], ["Shopify", data.pending_now.shopify], ["Mercado Libre", data.pending_now.mercadolibre], ["Amazon", data.pending_now.amazon]] as const).map(([l, v], i) => (
              <div key={l} className={`border p-4 ${i === 0 ? "border-amber-200 bg-amber-50" : "border-slate-200 bg-white"}`}>
                <div className="text-[10px] font-bold uppercase tracking-[0.13em] text-slate-500">{l}</div>
                <div className={`mt-2 text-xl font-medium tabular ${i === 0 ? "text-amber-700" : "text-slate-900"}`}>{money(v)}</div>
              </div>
            ))}
          </div>
          <PlatformMonthlyTable
            rows={data.months}
            selected={sel ? `${sel.month}|${sel.channel}` : undefined}
            onSelect={(month, channel) => setSel({ month, channel })}
          />
          {sel && <PlatformChargesDetail month={sel.month} channel={sel.channel} onClose={() => setSel(null)} />}
        </>
      )}
    </div>
  );
};

export default ReconciliationPage;
